import {
  FileText,
  Wallet,
  Coins,
  Gauge,
  TrendingUp,
  Hourglass,
  Handshake,
  ShieldAlert,
} from 'lucide-react';
import type { FunctionComponent } from 'react';
import { PolicyEducationSlide, type PolicyEducationConfig } from './policy-education-slide';

const money = (value?: number) =>
  value === undefined
    ? undefined
    : value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: value % 1 === 0 ? 0 : 2,
      });

const INTRO: PolicyEducationConfig = {
  eyebrow: 'Your Policy',
  title: "Let's explore how your policy works",
  icon: FileText,
  paragraphs: [
    'Your long-term care policy has a handful of key terms that determine how it actually works for you.',
    "We'll walk through what each one means and how your policy fits into the long-term care plan you just built.",
  ],
};

const MONTHLY_PREMIUM: PolicyEducationConfig = {
  eyebrow: 'Policy Term',
  title: 'Monthly premium',
  icon: Wallet,
  paragraphs: [
    'Your premium is what you pay to keep your policy active.',
    "It's the ongoing cost of having coverage in place, ready for the day you need care.",
  ],
  stats: details => [
    { label: 'Monthly premium', value: money(details.monthlyPremium), caption: 'per month' },
  ],
};

const BENEFIT_POOL: PolicyEducationConfig = {
  eyebrow: 'Policy Term',
  title: 'Total benefit pool',
  icon: Coins,
  paragraphs: [
    'Your benefit pool is the total amount your policy can pay out over the life of a claim.',
    'Think of it as the bucket of dollars available to help cover your care.',
  ],
  stats: details => [
    { label: 'Total benefit pool', value: money(details.benefitPool), caption: 'lifetime maximum' },
  ],
};

const BENEFIT_MAX: PolicyEducationConfig = {
  eyebrow: 'Policy Term',
  title: 'Daily & monthly benefit maximum',
  icon: Gauge,
  paragraphs: [
    'Your policy pays up to a set amount each day or month — your benefit maximum.',
    'How much of it you use can change depending on where you receive care: at home, in assisted living, or in a nursing home.',
  ],
  stats: details => [
    { label: 'Daily maximum', value: money(details.dailyBenefitMax), caption: 'per day' },
    { label: 'Monthly maximum', value: money(details.monthlyBenefitMax), caption: 'per month' },
  ],
};

const INFLATION_PROTECTION: PolicyEducationConfig = {
  eyebrow: 'Policy Term',
  title: 'Inflation protection',
  icon: TrendingUp,
  paragraphs: [
    'Care costs rise over time, so many policies include inflation protection to help your benefits keep pace.',
    'With compound inflation protection, your benefits grow on top of previous growth each year. With simple inflation protection, they grow by a fixed amount based on your original benefit.',
  ],
  stats: details => {
    if (details.inflationType === undefined) return [{ label: 'Inflation protection' }];
    if (details.inflationType === 'none') {
      return [
        {
          label: 'Inflation protection',
          value: 'None',
          caption: 'Benefits stay level over time',
        },
      ];
    }
    const typeLabel = details.inflationType === 'compound' ? 'Compound' : 'Simple';
    return [
      {
        label: 'Inflation protection',
        value: details.inflationRate !== undefined ? `${details.inflationRate}%` : typeLabel,
        caption: `${typeLabel} growth each year`,
      },
    ];
  },
};

const WAITING_PERIOD: PolicyEducationConfig = {
  eyebrow: 'Policy Term',
  title: 'Waiting period',
  icon: Hourglass,
  paragraphs: [
    "Before your benefits begin, there's a waiting period — sometimes called an elimination period.",
    "It's a set number of days you cover care yourself before your policy starts paying out.",
  ],
  stats: details => [
    {
      label: 'Waiting period',
      value:
        details.waitingPeriodDays !== undefined ? `${details.waitingPeriodDays} days` : undefined,
      caption: 'before benefits begin',
    },
  ],
};

const PARTNERSHIP: PolicyEducationConfig = {
  eyebrow: 'Policy Term',
  title: 'Partnership policies',
  icon: Handshake,
  paragraphs: [
    'Some policies are part of a state Partnership Program.',
    "These can let you protect more of your assets if you ever need to apply for Medicaid after using your policy's benefits.",
  ],
  stats: details => [
    {
      label: 'Partnership policy',
      value: details.isPartnership === undefined ? undefined : details.isPartnership ? 'Yes' : 'No',
      caption:
        details.isPartnership === undefined
          ? undefined
          : details.isPartnership
            ? 'Protects more of your assets under Medicaid'
            : 'Not part of a state Partnership Program',
    },
  ],
};

const RATE_ACTION: PolicyEducationConfig = {
  eyebrow: 'Policy Term',
  title: 'Rate actions',
  icon: ShieldAlert,
  paragraphs: [
    'You may have seen your premium increase over the years — this is called a rate action.',
    "It isn't about the carrier trying to make more money. Claims have come in much higher than anticipated, and rate actions help the carrier keep its promise to pay every dollar it owes.",
    "Policies like yours were often issued with very rich benefits for the price. We'll help you see how your benefits have changed over time, so you can make an informed decision about your coverage.",
  ],
  nextButtonLabel: 'Continue to Funding',
  stats: details => [
    { label: 'Original monthly premium', value: money(details.originalPremium) },
    { label: 'Today', value: money(details.monthlyPremium), caption: 'current premium' },
  ],
};

export function GenworthPolicyIntro(props: ClientIdProps) {
  return (
    <PolicyEducationSlide
      slidePage={GenworthPolicyIntro}
      clientId={props.clientId}
      config={INTRO}
    />
  );
}

export function GenworthPolicyMonthlyPremium(props: ClientIdProps) {
  return (
    <PolicyEducationSlide
      slidePage={GenworthPolicyMonthlyPremium}
      clientId={props.clientId}
      config={MONTHLY_PREMIUM}
    />
  );
}

export function GenworthPolicyBenefitPool(props: ClientIdProps) {
  return (
    <PolicyEducationSlide
      slidePage={GenworthPolicyBenefitPool}
      clientId={props.clientId}
      config={BENEFIT_POOL}
    />
  );
}

export function GenworthPolicyBenefitMax(props: ClientIdProps) {
  return (
    <PolicyEducationSlide
      slidePage={GenworthPolicyBenefitMax}
      clientId={props.clientId}
      config={BENEFIT_MAX}
    />
  );
}

export function GenworthPolicyInflationProtection(props: ClientIdProps) {
  return (
    <PolicyEducationSlide
      slidePage={GenworthPolicyInflationProtection}
      clientId={props.clientId}
      config={INFLATION_PROTECTION}
    />
  );
}

export function GenworthPolicyWaitingPeriod(props: ClientIdProps) {
  return (
    <PolicyEducationSlide
      slidePage={GenworthPolicyWaitingPeriod}
      clientId={props.clientId}
      config={WAITING_PERIOD}
    />
  );
}

export function GenworthPolicyPartnership(props: ClientIdProps) {
  return (
    <PolicyEducationSlide
      slidePage={GenworthPolicyPartnership}
      clientId={props.clientId}
      config={PARTNERSHIP}
    />
  );
}

export function GenworthPolicyRateAction(props: ClientIdProps) {
  return (
    <PolicyEducationSlide
      slidePage={GenworthPolicyRateAction}
      clientId={props.clientId}
      config={RATE_ACTION}
    />
  );
}

// Ordered policy-education slides, with their routes and progress-menu titles. The order
// here is the order they appear in the Genworth onboarding flow (before funding sources).
export const GENWORTH_POLICY_EDUCATION_SLIDE_DEFS: {
  component: FunctionComponent<ClientIdProps>;
  path: string;
  title: string;
}[] = [
  {
    component: GenworthPolicyIntro,
    path: '/clients/:clientId/onboarding/policy/overview',
    title: 'Your Policy',
  },
  {
    component: GenworthPolicyMonthlyPremium,
    path: '/clients/:clientId/onboarding/policy/monthly-premium',
    title: 'Monthly Premium',
  },
  {
    component: GenworthPolicyBenefitPool,
    path: '/clients/:clientId/onboarding/policy/benefit-pool',
    title: 'Benefit Pool',
  },
  {
    component: GenworthPolicyBenefitMax,
    path: '/clients/:clientId/onboarding/policy/benefit-maximum',
    title: 'Benefit Maximum',
  },
  {
    component: GenworthPolicyInflationProtection,
    path: '/clients/:clientId/onboarding/policy/inflation-protection',
    title: 'Inflation Protection',
  },
  {
    component: GenworthPolicyWaitingPeriod,
    path: '/clients/:clientId/onboarding/policy/waiting-period',
    title: 'Waiting Period',
  },
  {
    component: GenworthPolicyPartnership,
    path: '/clients/:clientId/onboarding/policy/partnership',
    title: 'Partnership Policies',
  },
  {
    component: GenworthPolicyRateAction,
    path: '/clients/:clientId/onboarding/policy/rate-action',
    title: 'Rate Actions',
  },
];
